// Scorify Pro paywall (2026-10-27). Full screen: it is shown while the
// server says the paywall is on for this user and they are not entitled
// (src/lib/paywall.js shouldShowPaywall), and it leaves when the next
// GET/POST /api/subscription answers entitled. "Not now" (onNotNow) hands the
// session to preview mode: the parent keeps every tab viewable, shows the
// banner, and reopens this screen from any requirePro action. Prices, currency and
// the trial come from StoreKit through RevenueCat (src/lib/purchases.js);
// nothing here hardcodes a price. Light theme tokens only. Copy carries no
// hyphens or dashes.
import { useState, useEffect, useCallback } from 'react';
import { T, withAlpha } from './theme';
import { getOfferings, purchase, restore, checkTrialEligibility } from './lib/purchases';
import {
  pickPackages, savingsPercent, priceCopy, ctaLabel, DISCLOSURE, VALUE_LINES, TERMS_URL, PRIVACY_URL,
  PURCHASE_RECEIVED_NOTICE, NO_SUBSCRIPTION_NOTICE, PRICES_FAILED_NOTICE, NOT_NOW_LABEL,
} from './lib/paywall';

const FONT = "'DM Sans', sans-serif";
const SERIF = "'DM Serif Display', serif";
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

const screen = {
  position: 'fixed', inset: 0, zIndex: 290, background: T.bg, color: T.text, fontFamily: FONT,
  overflowY: 'auto', WebkitOverflowScrolling: 'touch',
  padding: 'calc(env(safe-area-inset-top, 0px) + 28px) 20px calc(env(safe-area-inset-bottom, 0px) + 24px)',
};
const inner = { maxWidth: 420, margin: '0 auto', display: 'flex', flexDirection: 'column', minHeight: '100%' };
const linkBtn = { background: 'transparent', border: 'none', color: T.textMuted, fontSize: 13, fontFamily: FONT, cursor: 'pointer', padding: '8px 6px', textDecoration: 'underline', textUnderlineOffset: 3 };

function Spinner() {
  return (
    <span aria-hidden="true" style={{ display: 'inline-block', width: 16, height: 16, borderRadius: '50%', border: `2px solid ${withAlpha(T.surface, 0.5)}`, borderTopColor: T.surface, animation: 'pwSpin 0.8s linear infinite', verticalAlign: 'middle' }} />
  );
}

function PlanCard({ title, line, badge, selected, onSelect, disabled }) {
  return (
    <button type="button" onClick={onSelect} disabled={disabled} aria-pressed={selected} style={{
      width: '100%', textAlign: 'left', cursor: disabled ? 'default' : 'pointer', fontFamily: FONT,
      background: T.surface, borderRadius: 14, padding: '14px 16px', marginBottom: 10,
      border: `2px solid ${selected ? T.accent : T.border}`, boxShadow: selected ? `0 0 0 3px ${withAlpha(T.accent, 0.12)}` : 'none',
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <span aria-hidden="true" style={{ width: 20, height: 20, borderRadius: '50%', flexShrink: 0, border: `2px solid ${selected ? T.accent : T.borderStrong}`, background: selected ? T.accent : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        {selected && <span style={{ width: 8, height: 8, borderRadius: '50%', background: T.surface }} />}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{title}</span>
          {badge && <span style={{ fontSize: 11, fontWeight: 700, color: T.accent, background: withAlpha(T.accent, 0.1), borderRadius: 999, padding: '3px 8px' }}>{badge}</span>}
        </span>
        <span style={{ display: 'block', fontSize: 13, color: T.textMuted, marginTop: 3 }}>{line}</span>
      </span>
    </button>
  );
}

function Footer({ onRestore, onSignOut, onDeleteAccount, onNotNow, busy, showRestore }) {
  return (
    <div style={{ marginTop: 'auto', paddingTop: 18, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      {onNotNow && (
        <button type="button" onClick={onNotNow} disabled={!!busy} style={{ ...linkBtn, color: T.text, fontSize: 14, fontWeight: 600, textDecoration: 'none', padding: '10px 12px', marginBottom: 6 }}>{NOT_NOW_LABEL}</button>
      )}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
        <a href={TERMS_URL} target="_blank" rel="noreferrer" style={{ ...linkBtn, padding: '6px 4px' }}>Terms of Use</a>
        <a href={PRIVACY_URL} target="_blank" rel="noreferrer" style={{ ...linkBtn, padding: '6px 4px' }}>Privacy Policy</a>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
        {showRestore && <button type="button" onClick={onRestore} disabled={!!busy} style={linkBtn}>{busy === 'restore' ? 'Restoring…' : 'Restore purchases'}</button>}
        <button type="button" onClick={onSignOut} disabled={!!busy} style={linkBtn}>Sign out</button>
        <button type="button" onClick={onDeleteAccount} disabled={!!busy} style={{ ...linkBtn, color: withAlpha(T.danger, 0.75) }}>Delete account</button>
      </div>
    </div>
  );
}

export default function Paywall({ native, onSync, onSignOut, onDeleteAccount, onNotNow }) {
  const [plans, setPlans] = useState({ annual: null, monthly: null });
  const [eligibility, setEligibility] = useState({});
  const [loadState, setLoadState] = useState('loading'); // loading | ready | failed
  const [selected, setSelected] = useState('annual');
  const [busy, setBusy] = useState(null); // 'purchase' | 'restore' | null
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoadState('loading');
    setNotice('');
    try {
      const offering = await getOfferings();
      const picked = pickPackages(offering);
      if (!picked.annual && !picked.monthly) throw new Error('no packages in the current offering');
      const ids = [picked.annual?.product?.identifier, picked.monthly?.product?.identifier].filter(Boolean);
      let elig = {};
      try { elig = await checkTrialEligibility(ids); } catch (e) { console.log('paywall: eligibility check failed', e?.message || e); }
      setPlans(picked);
      setEligibility(elig);
      setSelected(picked.annual ? 'annual' : 'monthly');
      setLoadState('ready');
    } catch (e) {
      console.log('paywall: offerings failed', e?.message || e);
      setLoadState('failed');
    }
  }, []);

  useEffect(() => { if (native) load(); }, [native, load]);

  const pkg = plans[selected];
  const eligibleFor = (p) => !!(p && eligibility[p.product?.identifier] === true);
  const eligible = eligibleFor(pkg);
  const savings = savingsPercent(plans.annual?.product?.price, plans.monthly?.product?.price);

  const buy = async () => {
    if (!pkg || busy) return;
    setBusy('purchase');
    setNotice('');
    try {
      const result = await purchase(pkg);
      if (result.cancelled) return;
      // The server is the source of truth: POST re-fetches the subscriber from
      // RevenueCat and writes it, then answers entitled. The webhook may land
      // later than the receipt, so give it a few tries before asking for Restore.
      for (let attempt = 0; attempt < 4; attempt++) {
        const sub = await onSync();
        if (sub?.entitled) return; // the parent unmounts this screen
        if (attempt < 3) await wait(2000);
      }
      setNotice(PURCHASE_RECEIVED_NOTICE);
    } catch (e) {
      console.log('paywall: purchase failed', e?.message || e);
      setNotice('The purchase did not go through. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const doRestore = async () => {
    if (busy) return;
    setBusy('restore');
    setNotice('');
    try {
      await restore();
      const sub = await onSync();
      if (!sub?.entitled) setNotice(NO_SUBSCRIPTION_NOTICE);
    } catch (e) {
      console.log('paywall: restore failed', e?.message || e);
      setNotice('Restore did not finish. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  if (!native) {
    return (
      <div role="dialog" aria-modal="true" aria-label="Scorify Pro" style={screen}>
        <div style={inner}>
          <div style={{ fontFamily: SERIF, fontSize: 30, lineHeight: 1.15, marginBottom: 12 }}>Scorify Pro</div>
          <div style={{ fontSize: 15, color: T.textMuted, lineHeight: 1.6, marginBottom: 20 }}>
            Scorify Pro is available in the iOS app. Subscribe on your iPhone to keep using Scorify, then come back here.
          </div>
          <Footer onSignOut={onSignOut} onDeleteAccount={onDeleteAccount} onNotNow={onNotNow} busy={null} showRestore={false} />
        </div>
      </div>
    );
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Scorify Pro" style={screen}>
      <style>{'@keyframes pwSpin{to{transform:rotate(360deg)}}'}</style>
      <div style={inner}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.accent, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8 }}>Scorify Pro</div>
        <div style={{ fontFamily: SERIF, fontSize: 30, lineHeight: 1.15, marginBottom: 14 }}>Keep your numbers working for you.</div>
        <div style={{ marginBottom: 22 }}>
          {VALUE_LINES.map((line) => (
            <div key={line} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 15, color: T.text, lineHeight: 1.5, marginBottom: 6 }}>
              <span aria-hidden="true" style={{ color: T.accent, fontWeight: 700 }}>✓</span>
              <span>{line}</span>
            </div>
          ))}
        </div>

        {loadState === 'loading' && (
          <div style={{ fontSize: 14, color: T.textMuted, padding: '18px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: '50%', border: `2px solid ${T.border}`, borderTopColor: T.accent, animation: 'pwSpin 0.8s linear infinite' }} />
            Loading prices…
          </div>
        )}
        {loadState === 'failed' && (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px', marginBottom: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{PRICES_FAILED_NOTICE}</div>
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>Check your connection and try again.</div>
            <button type="button" onClick={load} style={{ background: T.accent, color: T.surface, border: 'none', borderRadius: 10, padding: '11px 18px', fontSize: 14, fontWeight: 700, fontFamily: FONT, cursor: 'pointer' }}>Retry</button>
          </div>
        )}
        {loadState === 'ready' && (
          <>
            {plans.annual && (
              <PlanCard title="Annual" badge={savings ? `Save ${savings}%` : null} selected={selected === 'annual'} onSelect={() => setSelected('annual')} disabled={!!busy}
                line={priceCopy({ period: 'annual', priceString: plans.annual.product?.priceString, eligible: eligibleFor(plans.annual) })} />
            )}
            {plans.monthly && (
              <PlanCard title="Monthly" selected={selected === 'monthly'} onSelect={() => setSelected('monthly')} disabled={!!busy}
                line={priceCopy({ period: 'monthly', priceString: plans.monthly.product?.priceString, eligible: eligibleFor(plans.monthly) })} />
            )}
            <button type="button" onClick={buy} disabled={!pkg || !!busy} style={{
              width: '100%', marginTop: 6, background: busy ? withAlpha(T.accent, 0.7) : T.accent, color: T.surface, border: 'none', borderRadius: 12,
              padding: '15px', fontSize: 16, fontWeight: 700, fontFamily: FONT, cursor: busy ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            }}>
              {busy === 'purchase' ? <><Spinner /> Working…</> : ctaLabel(eligible)}
            </button>
            <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.55, marginTop: 12 }}>{DISCLOSURE}</div>
          </>
        )}
        {notice && (
          <div role="status" style={{ marginTop: 14, fontSize: 13, color: T.text, background: withAlpha(T.accent, 0.08), border: `1px solid ${withAlpha(T.accent, 0.2)}`, borderRadius: 10, padding: '10px 12px', lineHeight: 1.5 }}>{notice}</div>
        )}

        <Footer onRestore={doRestore} onSignOut={onSignOut} onDeleteAccount={onDeleteAccount} onNotNow={onNotNow} busy={busy} showRestore />
      </div>
    </div>
  );
}
